import { Button } from '@/components/ui/Button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/Command'
import { SOC_OCCUPATIONS } from '@/data/soc-occupations'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

interface ComboboxProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  id?: string
}

export function RoleCombobox({ value, onChange, placeholder, className, id }: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const grouped = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return null

    const matches = SOC_OCCUPATIONS.filter(
      (o) =>
        o.title.toLowerCase().includes(q) ||
        o.aliases?.some((a) => a.toLowerCase().includes(q)),
    )
    if (matches.length === 0) return null

    const byGroup: Record<string, typeof matches> = {}
    for (const occ of matches.slice(0, 50)) {
      if (!byGroup[occ.group]) byGroup[occ.group] = []
      // biome-ignore lint/style/noNonNullAssertion: guard above ensures key exists
      byGroup[occ.group]!.push(occ)
    }
    return byGroup
  }, [search])

  const showFreeText = search.length > 1 && search.toLowerCase() !== value.toLowerCase()

  function handleSelect(title: string) {
    onChange(title)
    setOpen(false)
    setSearch('')
  }

  function handleFreeText() {
    onChange(search)
    setOpen(false)
    setSearch('')
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        id={id}
        type="button"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className="w-full justify-between font-normal"
      >
        <span className={cn('truncate', !value && 'text-muted-foreground')}>
          {value || placeholder || 'Search roles...'}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder="Type to search roles..."
              autoFocus
            />
            <CommandList>
              {showFreeText && (
                <>
                  <CommandGroup>
                    <CommandItem onSelect={handleFreeText} className="text-muted-foreground italic">
                      Use &ldquo;{search}&rdquo; as role →
                    </CommandItem>
                  </CommandGroup>
                  {grouped && <CommandSeparator />}
                </>
              )}

              {grouped ? (
                Object.entries(grouped).map(([group, occs]) => (
                  <CommandGroup key={group} heading={group}>
                    {occs.map((occ) => (
                      <CommandItem
                        key={occ.code + occ.title}
                        value={occ.title}
                        onSelect={() => handleSelect(occ.title)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value === occ.title ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {occ.title}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              ) : !search ? (
                <CommandEmpty className="text-muted-foreground">
                  Start typing to search 867 occupations...
                </CommandEmpty>
              ) : (
                <CommandEmpty>
                  No match —{' '}
                  <button
                    type="button"
                    onClick={handleFreeText}
                    className="underline underline-offset-2"
                  >
                    use &ldquo;{search}&rdquo;
                  </button>
                </CommandEmpty>
              )}
            </CommandList>
          </Command>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
      )}
    </div>
  )
}
